import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Agent } from '@/hooks/useAgents';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { POLLING_INTERVALS } from '@/sections/agents/constants';
import type { UseAgentJobsReturn } from '@/sections/agents/types/hooks.types';
import { Job, JobStatus, ResourceType } from '@/types';
import { useMergeStatus } from '@/utils/api';
import { getJobStatus } from '@/utils/job';

/**
 * Filters for agent-specific jobs
 */
interface AgentJobFilters {
  search: string;
  status: string;
  source: string;
}

/**
 * Default filter values
 */
const defaultFilters: AgentJobFilters = {
  search: '',
  status: '',
  source: '',
};

/**
 * Hook for fetching and managing jobs associated with a specific Aegis agent
 *
 * This hook retrieves all jobs executed by an Aegis agent. When an Aegis agent
 * executes a capability, the job's config.client_id field is populated with the
 * agent's client_id, allowing us to track all jobs executed by that specific agent.
 *
 * NOTE: Currently uses client-side filtering because the backend doesn't support
 * querying nested config fields. This may impact performance with large job sets.
 * A backend enhancement to support config field queries would improve efficiency.
 *
 * @param agent - The agent whose jobs to fetch (must have client_id)
 * @param filters - Search and filter criteria
 * @param options - Query options including polling control
 */
export const useAgentJobs = (
  agent: Agent | null,
  filters: AgentJobFilters = defaultFilters,
  options: {
    enabled?: boolean;
    pollingEnabled?: boolean;
  } = { enabled: true, pollingEnabled: false }
): UseAgentJobsReturn => {
  const [isFilteredDataFetching] = useState(false);
  const [debouncedSearch] = useDebounce(filters.search, 500);

  const isSearched = Boolean(filters.search);

  // Build query based on agent information
  const agentQuery = useMemo(() => {
    if (!agent) return undefined;

    // Build query parts (without #job prefix since label=job handles that)
    const queryParts: string[] = [];

    // Add OS-based source filter to reduce initial result set
    // This helps with pagination when there are many jobs in the system
    if (agent.os && !filters.source) {
      const os = agent.os.toLowerCase();
      if (os.includes('windows')) {
        queryParts.push('source:windows');
      } else if (os.includes('linux')) {
        queryParts.push('source:linux');
      }
      // If OS is unknown or neither Windows/Linux, query all jobs
    }

    // Add additional filters if specified (these ARE supported by the backend)
    if (filters.status) {
      queryParts.push(`status:${filters.status}`);
    }

    if (filters.source) {
      queryParts.push(`source:${filters.source}`);
    }

    // Return joined query parts or undefined to fetch all jobs
    return queryParts.length > 0 ? queryParts.join(' ') : undefined;
  }, [agent, filters.status, filters.source]);

  // Primary query for jobs targeting the agent's hostname
  const {
    data: primaryJobsData,
    refetch: refetchPrimaryJobs,
    error: primaryJobsError,
    status: primaryJobsStatus,
    isFetchingNextPage: isPrimaryJobsFetchingNextPage,
    fetchNextPage: fetchPrimaryJobsNextPage,
    isFetching: isPrimaryJobsFetching,
    hasNextPage: hasPrimaryJobsNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: agentQuery,
    },
    {
      enabled: Boolean(agent) && options.enabled && !isSearched,
      refetchInterval: options.pollingEnabled ? POLLING_INTERVALS.JOBS : false,
    }
  );

  // Search jobs by name when searching (will filter by client_id client-side)
  const {
    data: nameSearchData,
    refetch: refetchNameSearch,
    status: nameSearchStatus,
    error: nameSearchError,
    isFetching: isNameSearchFetching,
    fetchNextPage: fetchNameSearchNextPage,
    isFetchingNextPage: isNameSearchFetchingNextPage,
    hasNextPage: hasNameSearchNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `name:${debouncedSearch} #job`,
    },
    {
      enabled: isSearched && Boolean(agent) && options.enabled,
      refetchInterval: options.pollingEnabled ? POLLING_INTERVALS.JOBS : false,
    }
  );

  // Search jobs by source when searching (will filter by client_id client-side)
  const {
    data: sourceSearchData,
    refetch: refetchSourceSearch,
    status: sourceSearchStatus,
    error: sourceSearchError,
    isFetching: isSourceSearchFetching,
    fetchNextPage: fetchSourceSearchNextPage,
    isFetchingNextPage: isSourceSearchFetchingNextPage,
    hasNextPage: hasSourceSearchNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `source:${debouncedSearch} #job`,
    },
    {
      enabled: isSearched && Boolean(agent) && options.enabled,
      refetchInterval: options.pollingEnabled ? POLLING_INTERVALS.JOBS : false,
    }
  );

  // Merge status from all active queries
  const apiStatus = useMergeStatus(
    ...(isSearched
      ? [nameSearchStatus, sourceSearchStatus]
      : [primaryJobsStatus])
  );

  // Combine error states
  const error = isSearched
    ? nameSearchError || sourceSearchError
    : primaryJobsError;

  // Combine fetching states
  const isFetching = isSearched
    ? isNameSearchFetching || isSourceSearchFetching
    : isPrimaryJobsFetching;

  // Combine next page states
  const isFetchingNextPage = isSearched
    ? isNameSearchFetchingNextPage || isSourceSearchFetchingNextPage
    : isPrimaryJobsFetchingNextPage;

  const hasNextPage = isSearched
    ? hasNameSearchNextPage || hasSourceSearchNextPage
    : hasPrimaryJobsNextPage;

  // Fetch next page for all active queries
  const fetchNextPage = () => {
    if (isSearched) {
      fetchNameSearchNextPage();
      fetchSourceSearchNextPage();
    } else {
      fetchPrimaryJobsNextPage();
    }
  };

  // Refetch all active queries
  const refetch = () => {
    if (isSearched) {
      refetchNameSearch();
      refetchSourceSearch();
    } else {
      refetchPrimaryJobs();
    }
  };

  // Combine and deduplicate job data
  const data: Job[] = useMemo(() => {
    let allJobs: Job[];

    if (isSearched) {
      // Combine search results and deduplicate
      allJobs = [
        ...(nameSearchData?.jobs || []),
        ...(sourceSearchData?.jobs || []),
      ].reduce((acc, job: Job) => {
        if (!acc.find(existing => existing.key === job.key)) {
          acc.push(job);
        }
        return acc;
      }, [] as Job[]);
    } else {
      // Use primary job data
      allJobs = primaryJobsData?.jobs || [];
    }

    // Filter jobs by agent's client_id (client-side filtering since backend doesn't support nested field queries)
    if (agent) {
      allJobs = allJobs.filter(job => {
        // Check if this job was executed by the current agent
        // Jobs executed by Aegis agents have config.client_id set to the agent's client_id
        const jobConfig = job.config as Record<string, string> | undefined;
        return jobConfig?.client_id === agent.client_id;
      });
    }

    // Apply status filter if specified
    if (filters.status) {
      allJobs = allJobs.filter(job => getJobStatus(job) === filters.status);
    }

    return allJobs;
  }, [
    isSearched,
    agent,
    filters.status,
    nameSearchData?.jobs,
    sourceSearchData?.jobs,
    primaryJobsData?.jobs,
  ]);

  // Generate job statistics for the agent
  const jobStats = useMemo(() => {
    const stats = data.reduce(
      (acc, job) => {
        const status = getJobStatus(job);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<JobStatus, number>
    );

    return {
      total: data.length,
      ...stats,
    };
  }, [data]);

  // Get unique sources for filtering
  const availableSources = useMemo(() => {
    const sources = new Set(data.map(job => job.source));
    return Array.from(sources).sort();
  }, [data]);

  const status = isFilteredDataFetching ? 'pending' : apiStatus;

  return {
    data,
    refetch,
    status,
    fetchNextPage,
    error,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    jobStats,
    availableSources,
  };
};
